import { useDispatch, useSelector } from "react-redux";
import { Col, message, Row, Skeleton, Space, Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";
import { flatMap, isEmpty, keyBy } from "lodash";
import { ClockCircleOutlined } from "@ant-design/icons";
import moment from "moment/moment";
import {
  selectAvailableClients,
  selectUserRoles,
  updateClient,
} from "../../../../redux/slices/userSlice";
import { isPermissionPresent } from "../../../../utilities/generalUtility";
import K from "../../../../utilities/constants";
import { ParseNumber, useQueryState } from "../../../../hooks/useQueryState";
import ClientWorkflow from "../../../../redux/models/clientWorkflow";
import { ClientSelector } from "./clientSelector/clientSelector";
import Workflow from "../../../../redux/models/workflow";
import { ButtonGroup } from "../../../../common/components/buttonGroup/buttonGroup";
import { ListIcon } from "../../../../common/icons/ListIcon";
import { FlowIcon } from "../../../../common/icons/FlowIcon";
import { usePageFilter } from "../../../../hooks/usePageFilters";
import { WorkflowBoard } from "./workflowBoard/workflowBoard";
import { WorkflowStatuses } from "./workflowStatuses/workflowStatuses";
import { WorkflowStatusesLoader } from "./workflowStatuses/workflowStatusesLoader/workflowStatusesLoader";
import { WorkflowLegend } from "./workflowLegend/workflowLegend";
import { Flex } from "../../../../common/components/flex/flex";
import SomethingWentWrong from "../../../../common/components/errorBoundary/somethinWentWrong";
import { useTransitionCounts } from "./hooks/useTransitionCounts";
import { getCandidateStatusKey } from "../../../../utilities/candidate";

const Tabs = {
  subStatus: {
    key: "subStatus",
    component: WorkflowStatuses,
    loader: WorkflowStatusesLoader,
    getStatuses: (statuses) => statuses,
  },
  workflowBoard: {
    key: "workflowBoard",
    component: WorkflowBoard,
    loader: () => <Skeleton active paragraph={{ rows: 12 }} />,
    getStatuses: (statuses, { entryPoint }) => {
      const index = statuses.findIndex(
        (item) => item.id === entryPoint?.statusId,
      );

      return statuses.slice(index);
    },
  },
};

export const CandidateWorkflowContainer = () => {
  const dispatch = useDispatch();
  const clients = useSelector(selectAvailableClients);
  const userRoles = useSelector(selectUserRoles);
  const isSuperAdmin = isPermissionPresent(K.Permissions.SuperAdmin, userRoles);

  const defaultSelectedClient = clients[0]?.id ?? null;

  const [clientId, setClientId] = useQueryState(
    "clientId",
    defaultSelectedClient,
    ParseNumber,
  );

  const [workflowDataLoading, setWorkflowDataLoading] = useState(true);
  const [workflowData, setWorkflowData] = useState({
    lastModifiedAt: null,
    statuses: [],
  });

  const [transitions, setTransitions] = useState([]);
  const [entryPointsLoading, setEntryPointsLoading] = useState(true);
  const [entryPoints, setEntryPoints] = useState([]);
  const [isDataLoadFailed, setIsDataLoadFailed] = useState(false);

  const [viewType, setViewType] = usePageFilter(
    "candidate_workflow_view_type",
    Tabs.subStatus.key,
  );

  const [workflowType, setWorkflowType] = usePageFilter(
    "candidate_workflow_type",
    null,
  );

  const activeTab = Tabs[viewType];
  const ActiveTabComponent = activeTab.component;
  const ActiveTabLoader = activeTab.loader;

  const isLoading = workflowDataLoading || entryPointsLoading;

  const { statuses } = workflowData;
  const { lastModifiedAt } = workflowData;

  const assessmentStatusId = useMemo(
    () => statuses.find((s) => s.name === "Assessment")?.id ?? null,
    [statuses],
  );

  const entryPointsById = useMemo(
    () => keyBy(entryPoints, "id"),
    [entryPoints],
  );

  const selectedEntryPoint = entryPointsById[workflowType];

  const uniqueStatusTransitions = transitions.filter(
    (t) =>
      t.fromStatusId !== t.toStatusId || t.fromSubStatusId !== t.toSubStatusId,
  );

  const { incomingCounts, outgoingCounts } = useTransitionCounts(
    uniqueStatusTransitions,
  );

  const statusesToShow = useMemo(() => {
    const tabStatuses = activeTab.getStatuses(statuses, {
      entryPoint: selectedEntryPoint,
    });

    return tabStatuses.map((s) => ({
      ...s,
      subStatuses: s.subStatuses.map((ss) => {
        const subStatusId = getCandidateStatusKey(ss.statusId, ss.id);

        return {
          ...ss,
          incomingTransitionsCount: incomingCounts[subStatusId] ?? 0,
          outgoingTransitionsCount: outgoingCounts[subStatusId] ?? 0,
        };
      }),
    }));
  }, [activeTab, statuses, selectedEntryPoint, incomingCounts, outgoingCounts]);

  const statusesById = useMemo(
    () => keyBy(statusesToShow, "id"),
    [statusesToShow],
  );

  const subStatusesById = useMemo(
    () =>
      keyBy(flatMap(statusesToShow, "subStatuses"), (ss) =>
        getCandidateStatusKey(ss.statusId, ss.id),
      ),
    [statusesToShow],
  );

  const fetchWorkflowData = async () => {
    setWorkflowDataLoading(true);

    try {
      const [summaryRes, transitionsRes] =
        await ClientWorkflow.getWorkflowData(clientId);

      setWorkflowData(summaryRes.data);
      setTransitions(transitionsRes.data);
    } catch (error) {
      message.error("Failed to load workflow data");
      console.error(error);
      setIsDataLoadFailed(true);
    } finally {
      setWorkflowDataLoading(false);
    }
  };

  const onWorkflowUpdated = (data, transitions) => {
    const { isModified, ...workflowData } = data;
    dispatch(
      updateClient({
        id: clientId,
        updates: { isWorkflowModified: isModified },
      }),
    );
    setWorkflowData(workflowData);
    setTransitions(transitions);
  };

  const handlePositionUpdate = async (afterStatusId) => {
    if (!assessmentStatusId) return;
    try {
      const { data } = await ClientWorkflow.updateStatusPosition(
        clientId,
        assessmentStatusId,
        { after_status_id: afterStatusId },
      );
      const { transitions, ...workflowData } = data;
      onWorkflowUpdated({ ...workflowData }, transitions);
      message.success("Assessment position updated");
    } catch (error) {
      message.error("Failed to update Assessment position");
      console.error(error);
    }
  };

  useEffect(() => {
    if (!clients.some((c) => c.id === clientId)) {
      setClientId(defaultSelectedClient);
    }
  }, [clients]);

  useEffect(() => {
    fetchWorkflowData();
  }, [clientId]);

  useEffect(() => {
    const fetchEntryPoints = async () => {
      try {
        const { data } = await Workflow.getEntryPoints();
        setEntryPoints(data);

        if (!workflowType) {
          setWorkflowType(data[0].id);
        }
      } catch (error) {
        message.error("Failed to load workflow entry points");
        console.error(error);
        setIsDataLoadFailed(true);
      } finally {
        setEntryPointsLoading(false);
      }
    };

    fetchEntryPoints();
  }, []);

  if (isDataLoadFailed) {
    return <SomethingWentWrong />;
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Row gutter={[16, 16]} align="bottom">
        <Col flex="0 0 340px" style={{ minWidth: 0 }}>
          <ClientSelector
            clients={clients}
            value={clientId}
            onChange={setClientId}
            disabled={isLoading}
          />
        </Col>

        <Col>
          <Flex align="center" wrap="wrap" gap="16px">
            <ButtonGroup value={viewType} onChange={setViewType}>
              <ButtonGroup.Button value="subStatus" icon={<ListIcon />} />
              <ButtonGroup.Button value="workflowBoard" icon={<FlowIcon />} />
            </ButtonGroup>

            {activeTab.key === Tabs.workflowBoard.key &&
              !isEmpty(entryPoints) && (
                <>
                  <ButtonGroup value={workflowType} onChange={setWorkflowType}>
                    {entryPoints.map((e) => (
                      <ButtonGroup.Button key={e.id} value={e.id}>
                        {`${e.name} view`}
                      </ButtonGroup.Button>
                    ))}
                  </ButtonGroup>

                  <WorkflowLegend />
                </>
              )}

            {!isLoading && lastModifiedAt && (
              <Tooltip
                title={
                  <>
                    Last Modified:
                    <br />
                    {moment
                      .utc(lastModifiedAt)
                      .local()
                      .format("MM/DD/YYYY, LT")}
                  </>
                }
              >
                <ClockCircleOutlined style={{ fontSize: "16px" }} />
              </Tooltip>
            )}
          </Flex>
        </Col>
      </Row>

      {isLoading ? (
        <ActiveTabLoader />
      ) : (
        <ActiveTabComponent
          key={activeTab.key}
          statuses={statusesToShow}
          statusesById={statusesById}
          subStatusesById={subStatusesById}
          transitions={uniqueStatusTransitions}
          selectedEntryPoint={selectedEntryPoint}
          clientId={clientId}
          onWorkflowUpdated={onWorkflowUpdated}
          onPositionUpdate={isSuperAdmin ? handlePositionUpdate : undefined}
        />
      )}
    </Space>
  );
};
